import React from 'react';
import { Sparkles, Eye } from 'lucide-react';
import { cn } from '@/lib/utils';

interface GenerationModeToggleProps {
  mode: 'suggestion' | 'supervised';
  onChange: (mode: 'suggestion' | 'supervised') => void;
  compact?: boolean;
}

/**
 * Reusable toggle for generation mode selection.
 * Used in Single Post, Quick Generate, Browse Templates, and Bulk Generate forms.
 */
export const GenerationModeToggle: React.FC<GenerationModeToggleProps> = ({
  mode,
  onChange,
  compact = false,
}) => {
  if (compact) {
    return (
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onChange('suggestion')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors',
            mode === 'suggestion'
              ? 'bg-coral text-white'
              : 'bg-muted text-muted-foreground hover:bg-gray-200'
          )}
        >
          <Sparkles className="w-3.5 h-3.5" />
          Standard
        </button>
        <button
          type="button"
          onClick={() => onChange('supervised')}
          className={cn(
            'flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors',
            mode === 'supervised'
              ? 'bg-teal text-white'
              : 'bg-muted text-muted-foreground hover:bg-gray-200'
          )}
        >
          <Eye className="w-3.5 h-3.5" />
          Supervised
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground">Generation Mode</label>
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          onClick={() => onChange('suggestion')}
          className={cn(
            'p-3 rounded-lg border text-left transition-colors',
            mode === 'suggestion'
              ? 'border-coral bg-coral-light'
              : 'hover:bg-muted'
          )}
        >
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-coral" />
            <span className="font-medium text-sm">Standard</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Full auto-generation</p>
        </button>
        <button
          type="button"
          onClick={() => onChange('supervised')}
          className={cn(
            'p-3 rounded-lg border text-left transition-colors',
            mode === 'supervised'
              ? 'border-teal bg-teal-light'
              : 'hover:bg-muted'
          )}
        >
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4 text-teal" />
            <span className="font-medium text-sm">Supervised</span>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Review each section</p>
        </button>
      </div>
    </div>
  );
};
